import type { IHighPriorityEventItem, IHighPriorityEventBlock, HighPriorityRenderItem } from '../types';
import { DBO_V } from '@op/utils/format';

/**
 * 生成时间段唯一标识
 * 用于判断事件是否属于同一时间段
 */
export function getTimeBlockKey(event: IHighPriorityEventItem): string {
  const start = event.start_time || '';
  const end = event.end_time || '';
  return `${start}__${end}`;
}

/**
 * 格式化 ratio 显示
 * 正数加+号，保留2位小数
 */
export function formatRatio(ratio?: number): { value: string; arrow: string } | null {
  if (ratio === undefined) return null;
  // 使用绝对值格式化，不带正负号（箭头已表示方向）
  const value = DBO_V.percentageFormat(Math.abs(ratio), '-', 2);
  let arrow = '';
  if (ratio > 0) arrow = '↑';
  else if (ratio < 0) arrow = '↓';
  return { value, arrow };
}

/**
 * 计算块整体趋势
 * 根据所有事件的 ratio 判断整体趋势
 */
export function calculateBlockTrend(events: IHighPriorityEventItem[]): 'up' | 'down' | 'mixed' {
  const hasUp = events.some(e => (e.ratio ?? 0) >= 0);
  const hasDown = events.some(e => (e.ratio ?? 0) < 0);

  if (hasUp && hasDown) return 'mixed';
  if (hasDown) return 'down';
  return 'up';
}

/**
 * 计算块平均 ratio
 */
export function calculateAvgRatio(events: IHighPriorityEventItem[]): number | undefined {
  const validRatios = events
    .map(e => e.ratio)
    .filter((r): r is number => r !== undefined);

  if (validRatios.length === 0) return undefined;

  const sum = validRatios.reduce((acc, r) => acc + r, 0);
  return sum / validRatios.length;
}

/**
 * 创建事件块数据
 */
export function createEventBlock(events: IHighPriorityEventItem[]): IHighPriorityEventBlock {
  const firstEvent = events[0];
  const avgRatio = calculateAvgRatio(events);
  const blockTrend = calculateBlockTrend(events);
  const selected = events.some(e => e.selected);

  return {
    id: `block-${firstEvent.id}`,
    events,
    start_time: firstEvent.start_time || '',
    end_time: firstEvent.end_time,
    blockTrend,
    avgRatio,
    selected,
    isBlock: true,
  };
}

/**
 * 将高级事件数据转换为渲染项
 * 相同时间段的事件合并为块
 */
export function groupEventsToBlocks(
  events: Record<string, IHighPriorityEventItem[]>
): Record<string, HighPriorityRenderItem[]> {
  const result: Record<string, HighPriorityRenderItem[]> = {};

  Object.entries(events).forEach(([xAxisValue, eventList]) => {
    const groups = new Map<string, IHighPriorityEventItem[]>();

    // 按时间段分组
    eventList.forEach(event => {
      const key = getTimeBlockKey(event);
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(event);
    });

    // 转换为渲染项
    result[xAxisValue] = Array.from(groups.values()).map(group => {
      if (group.length === 1) {
        // 单个事件，保持原样
        return group[0];
      }
      // 多个事件，合并为块
      return createEventBlock(group);
    });
  });

  return result;
}

/**
 * 判断渲染项是否为事件块
 */
export function isEventBlock(item: HighPriorityRenderItem): item is IHighPriorityEventBlock {
  return 'isBlock' in item && item.isBlock === true;
}
